/**
 * Ditado → exames do atendimento (spec 009).
 *
 * O que a fila já validou de forma determinística (a refração da receita de
 * óculos) vai DIRETO pros módulos de exame, sem passar por IA de novo: é o
 * mesmo número, no mesmo formato que o painel grava ("-0,75", "180").
 *
 * O resto do que foi dito (PIO, acuidade, biometria…) vem no card "Exames
 * do atendimento" (23/09/2026, abaixo). Sem card, o "Preencher com IA" do
 * atendimento ainda roda sobre a transcrição, como fallback.
 *
 * É assim que o ditado de hoje aparece no rail "Última consulta" da próxima
 * avaliação. Pedido do Leopoldo, 17/09/2026.
 */

import { applyExamFill, parseExamFillResponse, type ExamFillMap } from '@/utils/encounterAutoFill';
import { BUILTIN_EXAM_MODULES } from '@/data/encounterCatalog';
import type { EncounterExamsState, ExamModuleDef } from '@/types/encounter';
import { formatDiopter, type VoiceEyeRx, type VoiceLensRx } from './normalize';
import type { VoiceDraft } from './resolveDrafts';
import { repairFindingText } from './examVocab';
import { isNormalCardField, isNormalExamModule, isNormalFinding, NORMAL_FINDING, saidNormalToFinding } from './examNormals';

/** Módulo unificado de refração (M3) e as chaves da linha "Subjetiva". */
export const REFRACAO_MODULE_ID = 'refracao';
const SUBJ = {
  esferica: 'subj_esferico',
  cilindrica: 'subj_cilindrico',
  eixo: 'subj_eixo',
  adicao: 'subj_adicao',
} as const;

function eyeCells(eye: VoiceEyeRx): Partial<Record<keyof typeof SUBJ, string>> {
  const out: Partial<Record<keyof typeof SUBJ, string>> = {};
  if (eye.esferica.value !== null) out.esferica = formatDiopter(eye.esferica.value);
  if (eye.cilindrica.value !== null) out.cilindrica = formatDiopter(eye.cilindrica.value);
  if (eye.eixo.value !== null) out.eixo = String(eye.eixo.value);
  if (eye.adicao.value !== null) out.adicao = formatDiopter(eye.adicao.value);
  return out;
}

/**
 * Receita de óculos ditada → mapa de preenchimento do módulo "Refração"
 * (linha subjetiva: é a receita que o médico prescreveu). Só entra campo
 * com valor; lacuna não vira célula.
 */
export function lensRxToExamFill(lens: VoiceLensRx): ExamFillMap {
  const od = eyeCells(lens.od);
  const oe = eyeCells(lens.oe);
  const fields: ExamFillMap[string] = {};
  for (const k of Object.keys(SUBJ) as Array<keyof typeof SUBJ>) {
    const cell: { OD?: string; OE?: string } = {};
    if (od[k]) cell.OD = od[k];
    if (oe[k]) cell.OE = oe[k];
    if (cell.OD || cell.OE) fields[SUBJ[k]] = cell;
  }
  return Object.keys(fields).length > 0 ? { [REFRACAO_MODULE_ID]: fields } : {};
}

/**
 * Bloco antigo "Refração Subjetiva Dinâmica": saiu do catálogo no M3, mas
 * segue nos modelos de ficha que já o usam (ex.: ROTINA). Ficha com ele e
 * sem o "Refração" novo → a subjetiva ditada entra NELE, que é onde o médico
 * olha (pedido de 01/10/2026: "não estou vendo na ficha").
 */
export const LEGACY_REFRACAO_MODULE_ID = 'refracao-subj-dinamica';
const LEGACY_KEY: Record<string, string> = {
  [SUBJ.esferica]: 'esferico',
  [SUBJ.cilindrica]: 'cilindrico',
  [SUBJ.eixo]: 'eixo',
  [SUBJ.adicao]: 'adicao',
};

/** Leva a subjetiva do mapa pro bloco de refração que a ficha já tem. */
export function fitRefractionToRecord(fill: ExamFillMap, state: EncounterExamsState): ExamFillMap {
  const ref = fill[REFRACAO_MODULE_ID];
  if (!ref) return fill;
  const ids = new Set(state.entries.map((e) => e.moduleId));
  if (!ids.has(LEGACY_REFRACAO_MODULE_ID) || ids.has(REFRACAO_MODULE_ID)) return fill;
  const legacy: ExamFillMap[string] = { ...(fill[LEGACY_REFRACAO_MODULE_ID] ?? {}) };
  const rest: ExamFillMap[string] = {};
  for (const [key, cell] of Object.entries(ref)) {
    if (LEGACY_KEY[key]) legacy[LEGACY_KEY[key]] = { ...(legacy[LEGACY_KEY[key]] ?? {}), ...cell };
    else rest[key] = cell;
  }
  const out: ExamFillMap = { ...fill, [LEGACY_REFRACAO_MODULE_ID]: legacy };
  if (Object.keys(rest).length > 0) out[REFRACAO_MODULE_ID] = rest;
  else delete out[REFRACAO_MODULE_ID];
  return out;
}

/**
 * Junta a parte determinística de todos os cards da fila: os cards "Exames
 * do atendimento" e a refração da receita de óculos (que manda sobre uma
 * refração ditada como exame — é a receita assinada).
 */
export function draftsToExamFill(drafts: VoiceDraft[]): ExamFillMap {
  let out: ExamFillMap = {};
  for (const d of drafts) {
    if (d.kind === 'examesAtendimento') out = mergeExamFill(out, d.fill);
  }
  for (const d of drafts) {
    if (d.kind === 'receitaOculos') out = mergeExamFill(out, lensRxToExamFill(d.lens));
  }
  return out;
}

// ─── Card "Exames do atendimento" (pedido de 23/09/2026) ───
//
// Os valores de exame DITADOS (PIO, AV, refração, biometria…) viram um card
// na fila, igual às receitas e solicitações, e entram nos módulos do
// atendimento assim que o card nasce ou muda. Fonte: o campo
// `examesAtendimento` da extração (push-to-talk) ou da função do ao vivo —
// sempre validado contra o catálogo por `parseExamFillResponse`.

/** Módulos que o ditado pode preencher: os de medição ativos (mesmo recorte do "Extrair Dados"). */
export const VOICE_EXAM_MODULES: ExamModuleDef[] = BUILTIN_EXAM_MODULES.filter(
  (m) => m.category !== 'diagnostico' && m.category !== 'anamnese' && !m.deprecated && !m.fromHistory,
);
const MODULE_BY_ID = new Map(VOICE_EXAM_MODULES.map((m) => [m.id, m]));
/** Onde o ditado pode GRAVAR: os de cima + o bloco antigo de refração (só
 *  quando a ficha já o tem — ver `fitRefractionToRecord`). */
export const VOICE_APPLY_MODULES: ExamModuleDef[] = [
  ...VOICE_EXAM_MODULES,
  ...BUILTIN_EXAM_MODULES.filter((m) => m.id === LEGACY_REFRACAO_MODULE_ID),
];

/**
 * Achado descritivo (biomicroscopia, fundoscopia…: campo de TEXTO por olho)
 * dito sem nomear o olho vale pelos dois — é assim que o oftalmologista dita
 * ("fundoscopia: disco normocorado, mácula e vasos sem alterações").
 *
 * Até 02/10/2026 esse achado sumia calado: o modelo mandava "mono" (ou valor
 * solto) num campo por olho e o parser descartava, sem card nem pendência —
 * o médico repetia até dizer "em ambos os olhos". Número sem olho (PIO, AV)
 * continua descartado: ali chutar o lado seria dado errado.
 */
function isEyeTextField(moduleId: string, fieldKey: string): boolean {
  const def = MODULE_BY_ID.get(moduleId)?.fields.find((f) => f.key === fieldKey);
  return !!def && (def.layout ?? 'eyes') === 'eyes' && def.kind === 'textarea';
}

/** Qualquer coisa que a IA devolveu em `examesAtendimento` → mapa validado. */
export function toVoiceExamFill(raw: unknown): ExamFillMap {
  if (!raw || typeof raw !== 'object') return {};
  // Achado sem olho (valor solto ou só "mono") → OD e OE, antes do parser
  // que descarta valor escalar em campo por olho.
  const fixed: Record<string, unknown> = {};
  for (const [mod, fields] of Object.entries(raw as Record<string, unknown>)) {
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
      fixed[mod] = fields;
      continue;
    }
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(fields as Record<string, unknown>)) {
      if (!isEyeTextField(mod, key)) {
        out[key] = val;
        continue;
      }
      // Troca de áudio já vista ("normal, corado", "macrovasos") → o termo dito.
      // "Normal" dito → o normal do card (caixa marcada), não um achado "Normal".
      // "Normal, exceto …" → o normal com o achado no lugar da estrutura.
      const conserta = (v: unknown) =>
        typeof v === 'string' ? normalWithException(mod, saidNormalToFinding(mod, repairFindingText(mod, v))) : v;
      const cell = val && typeof val === 'object' && !Array.isArray(val) ? (val as Record<string, unknown>) : null;
      const solto = conserta(cell ? cell.mono : val);
      const temOlho = !!cell && ['OD', 'OE', 'od', 'oe'].some((k) => typeof cell[k] === 'string' && (cell[k] as string).trim());
      out[key] = !temOlho && typeof solto === 'string' && solto.trim()
        ? { OD: solto, OE: solto }
        : cell ? Object.fromEntries(Object.entries(cell).map(([k, v]) => [k, conserta(v)])) : val;
    }
    fixed[mod] = out;
  }
  return parseExamFillResponse(JSON.stringify(fixed), VOICE_EXAM_MODULES);
}

/**
 * Formato da função do ao vivo → formato da extração. O Live não aceita
 * objeto de chaves livres no schema, então lá é uma lista
 * `[{modulo, campo, olho, valor}]`. Nada é validado aqui (é do parser).
 */
export function examListToFill(list: unknown): Record<string, Record<string, Record<string, string>>> {
  const out: Record<string, Record<string, Record<string, string>>> = {};
  if (!Array.isArray(list)) return out;
  for (const it of list) {
    if (!it || typeof it !== 'object') continue;
    const o = it as Record<string, unknown>;
    const modulo = typeof o.modulo === 'string' ? o.modulo.trim() : '';
    const campo = typeof o.campo === 'string' ? o.campo.trim() : '';
    const valor = typeof o.valor === 'string' ? o.valor.trim() : typeof o.valor === 'number' ? String(o.valor) : '';
    const olhoRaw = typeof o.olho === 'string' ? o.olho.trim().toUpperCase() : '';
    const olhos = olhoRaw === 'AO' ? ['OD', 'OE'] : olhoRaw === 'OD' || olhoRaw === 'OE' ? [olhoRaw] : ['mono'];
    if (!modulo || !campo || !valor) continue;
    const cell = ((out[modulo] ??= {})[campo] ??= {});
    for (const olho of olhos) cell[olho] = valor;
  }
  return out;
}

/**
 * Junta dois mapas; célula repetida fica com a do `incoming` (ditar de novo é
 * corrigir).
 *
 * Achado em TEXTO soma (02/10/2026): o Ao vivo chama a cada fôlego, e
 * "córnea transparente" … "íris trófica, fácico" chegavam em duas chamadas —
 * a segunda apagava a primeira do card. Ver `mergeFindingText`.
 */
export function mergeExamFill(existing: ExamFillMap, incoming: ExamFillMap): ExamFillMap {
  const out: ExamFillMap = {};
  for (const src of [existing, incoming]) {
    for (const [mod, fields] of Object.entries(src)) {
      const m = (out[mod] ??= {});
      for (const [key, cell] of Object.entries(fields)) {
        const antes = m[key] ?? {};
        const junto = { ...antes, ...cell };
        if (TEXT_FIELDS.get(mod)?.has(key)) {
          for (const eye of ['OD', 'OE', 'mono'] as const) {
            const a = antes[eye];
            const b = cell[eye];
            if (a && b) junto[eye] = mergeFindingText(mod, a, b);
          }
        }
        m[key] = junto;
      }
    }
  }
  return out;
}

/** Tira do mapa os módulos indicados (os que a ancoragem não sustentou). */
export function dropExamModules(fill: ExamFillMap, ids: string[]): ExamFillMap {
  const fora = new Set(ids);
  return Object.fromEntries(Object.entries(fill).filter(([id]) => !fora.has(id)));
}

/** Quantas células (OD/OE/valor único) o mapa carrega. */
export function countExamCells(fill: ExamFillMap): number {
  let n = 0;
  for (const fields of Object.values(fill)) {
    for (const c of Object.values(fields)) n += (c.OD ? 1 : 0) + (c.OE ? 1 : 0) + (c.mono ? 1 : 0);
  }
  return n;
}

/** Rótulo do módulo, pra card e pendência. */
export function examModuleLabel(moduleId: string): string {
  return MODULE_BY_ID.get(moduleId)?.label ?? moduleId;
}

/** Linhas legíveis do card: módulo → campo → OD/OE/valor. */
export interface ExamFillRow {
  moduleId: string;
  moduleLabel: string;
  fields: Array<{ key: string; label: string; suffix?: string; OD?: string; OE?: string; mono?: string }>;
}
export function describeExamFill(fill: ExamFillMap): ExamFillRow[] {
  return Object.entries(fill).map(([moduleId, fields]) => {
    const mod = MODULE_BY_ID.get(moduleId);
    const defs = new Map((mod?.fields ?? []).map((f) => [f.key, f]));
    return {
      moduleId,
      moduleLabel: mod?.label ?? moduleId,
      fields: Object.entries(fields).map(([key, cell]) => ({
        key,
        label: defs.get(key)?.label ?? key,
        suffix: defs.get(key)?.suffix,
        ...cell,
      })),
    };
  });
}

/**
 * Palavras que sustentam, na fala, que aquele exame foi mesmo ditado. Além
 * delas vale o nome/sigla do módulo (ver `grounding.ts`). Palavra de até 3
 * letras só casa inteira ("av" não pode casar com "avaliação").
 */
export const EXAM_MODULE_ALIASES: Record<string, string[]> = {
  pio: ['pressao', 'pio', 'tonometr', 'tensao', 'goldmann'],
  'pio-sopro': ['sopro', 'pressao', 'pio', 'tonometr'],
  'acuidade-visual': ['acuidade', 'visao', 'av', '20/', 'jaeger', 'snellen', 'enxerga', 'conta dedos', 'vultos'],
  refracao: ['refra', 'esferic', 'cilindric', 'eixo', 'adicao', 'grau', 'dioptria', 'dinamic', 'estatic', 'subjetiv', 'objetiv'],
  lensometria: ['lensometr', 'oculos', 'lente'],
  ceratometria: ['ceratometr', 'queratometr', 'k1', 'k2', 'curvatura'],
  biometria: ['biometr', 'axial', 'comprimento', 'lio'],
  paquimetria: ['paquimetr', 'espessura', 'micra', 'micras'],
  fundoscopia: ['fundo', 'fundoscop', 'retina', 'papila', 'macula', 'vitreo'],
  // Módulo próprio desde 26/10/2026 ("mapeamento" saiu da fundoscopia).
  'mapeamento-retina': ['mapeamento', 'oftalmoscopia indireta', 'periferia', 'periferica'],
  'relacao-escavacao-disco': ['escava', 'disco', 'papila'],
  biomicroscopia: ['biomicro', 'lampada', 'cornea', 'cristalino', 'camara', 'conjuntiva', 'catarata', 'iris', 'palpebra'],
  gonioscopia: ['gonio', 'angulo'],
  motilidade: ['motilidade', 'movimenta', 'versoes', 'ducoes'],
  'cover-test': ['cover', 'desvio', 'foria', 'tropia'],
  schirmer: ['schirmer', 'chirmer', 'lagrima'],
  but: ['but', 'break', 'ruptura'],
};

// ─── Fusão de texto livre (pedido de 25/09/2026) ───
//
// Campo de achados (biomicroscopia, fundoscopia…) quase nunca está vazio:
// vem do modelo/última consulta ("Conjuntiva calma, córnea transparente…").
// Em vez de pular o campo, o achado ditado entra NO texto: substitui o trecho
// da mesma estrutura (dito hoje vale mais que o modelo) ou é acrescentado.
// Sem IA — o texto ditado entra exato, só muda onde ele fica.

/** Estruturas por módulo, na ordem em que são testadas (primeira que casa vence). */
const TEXT_STRUCTURES: Record<string, Array<[string, string[]]>> = {
  biomicroscopia: [
    ['palpebra', ['palpebr', 'blefar', 'meibom', 'calazio', 'hordeol', 'ptose', 'entropio', 'ectropio', 'triquiase']],
    ['conjuntiva', ['conjuntiv', 'pterigi', 'pinguecul', 'hiperemi', 'quemose', 'hiposfagma']],
    ['cornea', ['cornea', 'cerat', 'leucoma', 'fluoresce']],
    ['camara', ['camara', 'tyndall', 'hipopio', 'hifema']],
    ['iris', ['iris', 'rubeose', 'sinequia']],
    ['pupila', ['pupil', 'fotorreag', 'isocor', 'discor']],
    ['cristalino', ['cristalin', 'catarata', 'facia', 'facic', 'faco', 'lio', 'capsula']],
  ],
  fundoscopia: [
    ['vitreo', ['vitre', 'hialoid']],
    // A rima anda com a escavação, como no normal da clínica ("escavação
    // papilar com boa rima neurorretiniana"): "rima afinada" troca esse trecho,
    // não o "disco óptico normocorado" (25/10/2026).
    ['escavacao', ['escava', 'e/d', 'c/d', 'rima']],
    ['disco', ['disco', 'papil', 'nervo optico']],
    ['macula', ['macul', 'fove', 'drusa', 'epr', 'epirretin']],
    ['vasos', ['vaso', 'arteri', 'venos', 'veia', 'cruzamento']],
    ['retina', ['retin', 'periferi', 'rotura', 'descolamento', 'aplicad', 'hemorragi', 'exsudat', 'microaneurism']],
  ],
  'mapeamento-retina': [
    ['midriase', ['midriase', 'depressao escleral', 'indenta']],
    ['vitreo', ['vitre', 'hialoid', 'weiss', 'tracao']],
    ['degeneracao', ['lattice', 'palicada', 'pavimentos', 'branco sem pressao', 'degenera', 'squise']],
    ['rotura', ['rotura', 'buraco', 'ferradura', 'operculo', 'rasgadura']],
    ['laser', ['laser', 'fotocoagul', 'cicatriz', 'bloqueio']],
    ['retina', ['retin', 'periferi', 'descolamento', 'aplicad', 'hemorragi']],
  ],
};

/**
 * Estrutura vista que sustenta, na fala, o achado daquele módulo: o médico
 * descreve "pterígio nasal", "escavação 0,4", "retina aplicada" sem dizer
 * "biomicroscopia"/"fundoscopia" (pedido de 02/10/2026). Sem isto a
 * ancoragem tirava do card o achado ditado assim.
 *
 * Só pra ANCORAR (`grounding.ts`). O "tira a …" do Ao vivo continua casando
 * pelos apelidos de `EXAM_MODULE_ALIASES` — senão "tira a escavação" levaria
 * a fundoscopia inteira junto com a relação E/D.
 */
export const EXAM_FINDING_WORDS: Record<string, string[]> = Object.fromEntries(
  Object.entries(TEXT_STRUCTURES).map(([mod, estruturas]) => [mod, estruturas.flatMap(([id, palavras]) => [id, ...palavras])]),
);

/** Frase de "tudo normal" — dita ou no campo, vale pelo campo inteiro. */
const GLOBAL_NORMAL = /^(sem alteracoes|sem particularidades|normal|nda|dentro da normalidade)$/;

const norm = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

function structureOf(moduleId: string, segment: string): string | null {
  const t = norm(segment);
  for (const [id, words] of TEXT_STRUCTURES[moduleId] ?? []) {
    // Palavra de até 3 letras só casa inteira ("lio" não casa com "folio").
    if (words.some((w) => (w.length <= 3 ? new RegExp(`(^|[^a-z])${w}([^a-z]|$)`).test(t) : t.includes(w)))) return id;
  }
  return null;
}

interface Seg { text: string; sep: string; novo?: boolean; velho?: boolean }

/** Quebra em trechos por "," ";" "." e quebra de linha — "0,3" e "0.3" não quebram. */
function splitSegments(s: string): { segs: Seg[]; trailing: string } {
  const parts = s.split(/(\s*;\s*|\s*\n\s*|,\s+|\.(?:\s+|$))/);
  const segs: Seg[] = [];
  let trailing = '';
  for (let i = 0; i < parts.length; i += 2) {
    const text = parts[i].trim();
    // Separador normalizado: pontuação + espaço, ou + quebra de linha se havia.
    const raw = parts[i + 1] ?? '';
    const punct = raw.trim();
    const sep = punct + (raw.includes('\n') ? '\n' : punct ? ' ' : '');
    if (!text) continue;
    segs.push({ text, sep });
  }
  if (segs.length > 0) {
    trailing = segs[segs.length - 1].sep.startsWith('.') ? '.' : '';
    segs[segs.length - 1].sep = '';
  }
  return { segs, trailing };
}

/**
 * Trecho com duas estruturas ditas juntas ("escavação aumentada e drusas
 * maculares") vira dois, pra cada uma trocar o seu trecho. Só com 2+ palavras
 * em cada lado: "mácula e vasos com …" fica inteiro.
 */
function splitByStructure(moduleId: string, seg: Seg): Seg[] {
  const partes = seg.text.split(/\s+e\s+/);
  if (partes.length < 2 || partes.some((p) => p.split(/\s+/).length < 2)) return [seg];
  const ests = partes.map((p) => structureOf(moduleId, p));
  if (ests.some((e) => !e) || new Set(ests).size < ests.length) return [seg];
  return partes.map((text, i) => ({ text, sep: i < partes.length - 1 ? ', ' : seg.sep }));
}

/**
 * "mácula e vasos sem alterações" → "mácula sem alterações", "vasos sem
 * alterações" quando o ditado atinge uma das duas — senão "drusas maculares"
 * levava os vasos junto, ou "vasos tortuosos" ficava ao lado de "vasos sem
 * alterações".
 */
function splitCompound(moduleId: string, seg: Seg, est: string): Seg[] {
  const m = /^(\S+)\s+e\s+(\S+)\s+(.+)$/.exec(seg.text);
  if (!m) return [seg];
  const a = structureOf(moduleId, m[1]);
  const b = structureOf(moduleId, m[2]);
  if (!a || !b || a === b || (a !== est && b !== est)) return [seg];
  return [
    { ...seg, text: `${m[1]} ${m[3]}`, sep: ', ' },
    { ...seg, text: `${m[2]} ${m[3]}` },
  ];
}

const upperFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** Minúscula no começo, menos sigla ("LIO", "EPR"). */
const lowerFirst = (s: string) => (/^[A-ZÀ-Ý]{2}/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1));

function joinSegments(segs: Seg[], trailing: string): string {
  let out = '';
  segs.forEach((s, i) => {
    const prevSep = i === 0 ? '' : segs[i - 1].sep || ', ';
    let text = s.text;
    if (i === 0) text = upperFirst(text);
    else if (s.novo) text = prevSep.startsWith('.') || prevSep.includes('\n') ? upperFirst(text) : lowerFirst(text);
    out += prevSep;
    out += text;
  });
  return out + trailing;
}

/**
 * Funde o achado ditado no texto que já está no campo. Cada trecho ditado
 * substitui o(s) trecho(s) da mesma estrutura; estrutura nova vai pro fim.
 * Trecho que já está lá (mesmo texto) não duplica.
 *
 * `previous` é o que o ditado anterior pôs nesse campo: ditar de novo é
 * corrigir, então o trecho novo entra no LUGAR do antigo.
 */
export function fuseFindingText(moduleId: string, existing: string, dictated: string, previous = ''): string {
  const dito = splitSegments(dictated).segs.flatMap((s) => splitByStructure(moduleId, s));
  if (dito.length === 0) return existing;
  const antigos = new Set(splitSegments(previous).segs.map((s) => norm(s.text)));
  const { segs: todos, trailing } = splitSegments(existing);
  const segs = todos.map((s) => (antigos.has(norm(s.text)) ? { ...s, velho: true } : s));
  const tudoNormal = (list: Seg[]) => list.length === 1 && GLOBAL_NORMAL.test(norm(list[0].text));
  if (segs.every((s) => s.velho) || tudoNormal(segs) || tudoNormal(dito)) {
    return joinSegments(dito.map((s, i) => ({ ...s, sep: i < dito.length - 1 ? s.sep : '' })), '');
  }

  let out: Seg[] = segs.map((s) => ({ ...s }));
  for (const d of dito) {
    const igual = out.findIndex((s) => norm(s.text) === norm(d.text));
    if (igual !== -1) {
      out[igual] = { ...out[igual], velho: false };
      continue;
    }
    const novo: Seg = { text: d.text, sep: ', ', novo: true };
    const antigo = out.findIndex((s) => s.velho);
    if (antigo !== -1) {
      out[antigo] = { ...novo, sep: out[antigo].sep };
      continue;
    }
    const est = structureOf(moduleId, d.text);
    if (est) out = out.flatMap((s) => (s.novo ? [s] : splitCompound(moduleId, s, est)));
    const idx = est ? out.findIndex((s) => !s.novo && structureOf(moduleId, s.text) === est) : -1;
    if (idx === -1) {
      // Segundo achado da mesma estrutura ("escavação 0,7, rima afinada
      // inferior") entra logo depois do primeiro, não no fim do texto.
      let junto = -1;
      if (est) out.forEach((s, i) => { if (s.novo && structureOf(moduleId, s.text) === est) junto = i; });
      if (junto !== -1) {
        out.splice(junto + 1, 0, { ...novo, sep: out[junto].sep });
        out[junto] = { ...out[junto], sep: ', ' };
        continue;
      }
      if (out.length > 0 && !out[out.length - 1].sep) out[out.length - 1] = { ...out[out.length - 1], sep: ', ' };
      out.push(novo);
      continue;
    }
    // O primeiro trecho da estrutura vira o ditado (mantém o separador dele);
    // os outros da mesma estrutura saem, pra não sobrar "cristalino
    // transparente" ao lado de "catarata".
    novo.sep = out[idx].sep;
    out = out.flatMap((s, i) => {
      if (i === idx) return [novo];
      if (!s.novo && structureOf(moduleId, s.text) === est) return [];
      return [s];
    });
  }
  out = out.filter((s) => !s.velho);
  if (out.length > 0) out[out.length - 1] = { ...out[out.length - 1], sep: '' };
  return joinSegments(out, trailing);
}

// ─── "Normal, exceto …" (pedido de 25/10/2026) ───
//
// "Fundoscopia normal, exceto pela presença de escavação papilar aumentada"
// → "Disco óptico normocorado, escavação papilar aumentada, mácula e vasos
// sem alterações." O normal da clínica com o achado no lugar do trecho da
// mesma estrutura (`fuseFindingText`), sem IA.

/** Palavra que abre a exceção. */
const EXCECAO = /^(.*?)(?<!\p{L})(?:exceto|salvo|com exce[çc][ãa]o|a n[ãa]o ser|mas|por[ée]m)(?!\p{L})(.*)$/isu;
/** O que liga a palavra ao achado: "pela presença de", "por", "da"… */
const LIGACAO = /^[\s,;:.-]*(?:(?:pel[oa]s?|por|com|d[aeo]s?|n[oa]s?|em|[oa]s?)\s+)?(?:(?:presen[çc]a|achados?)\s+(?:de|d[aeo]s?)\s+)?/iu;

/**
 * Biomicroscopia/fundoscopia ditas normais com exceção → o normal do card
 * com a exceção dentro. A exceção solta ("exceto pela escavação aumentada",
 * dita noutra frase) também parte do normal. Qualquer outro texto passa
 * intacto.
 */
export function normalWithException(moduleId: string, text: string): string {
  if (!isNormalExamModule(moduleId)) return text;
  const m = EXCECAO.exec(text);
  if (!m) return text;
  const normal = NORMAL_FINDING[moduleId];
  const antes = m[1];
  if (antes.replace(/[\s,;:.]/g, '') && saidNormalToFinding(moduleId, antes) !== normal) return text;
  const achado = m[2].replace(LIGACAO, '').trim();
  return achado ? fuseFindingText(moduleId, normal, achado) : normal;
}

/**
 * Achado novo no card que já tinha achado do mesmo módulo e olho. Se repete
 * algum trecho igual, é o achado ditado de novo, inteiro: vale o novo (é
 * assim que se corrige "lente transparente" por "fácico"). Senão é
 * continuação ou correção de uma estrutura ("não, a córnea tem leucoma"):
 * funde como no prontuário — mesma estrutura troca, estrutura nova soma.
 */
function mergeFindingText(moduleId: string, antes: string, agora: string): string {
  // Normal marcado no card (biomicroscopia/fundoscopia, 14/10/2026): o achado
  // ditado daquele olho prevalece — troca, não funde.
  if (isNormalFinding(moduleId, antes)) return agora;
  // E o normal ditado depois de um achado também troca: ditar de novo é corrigir.
  if (isNormalFinding(moduleId, agora)) return agora;
  const ja = new Set(splitSegments(antes).segs.map((s) => norm(s.text)));
  if (splitSegments(agora).segs.some((s) => ja.has(norm(s.text)))) return agora;
  return fuseFindingText(moduleId, antes, agora);
}

/** Campos de texto livre de cada módulo — esses fundem em vez de pular. */
const TEXT_FIELDS = new Map(
  VOICE_EXAM_MODULES.map((m) => [m.id, new Set(m.fields.filter((f) => f.kind === 'textarea').map((f) => f.key))]),
);

/** Campo de texto livre (achados de biomicroscopia, fundoscopia…)? */
export function isTextFindingField(moduleId: string, key: string): boolean {
  return !!TEXT_FIELDS.get(moduleId)?.has(key);
}

/**
 * Leva o card aos módulos do atendimento.
 *
 * Não-destrutivo como o "Preencher com IA": célula que o médico digitou fica.
 * A exceção é a célula que ainda tem o valor que o PRÓPRIO ditado pôs da
 * última vez (`prevPushed`) — ditar de novo corrige, como na receita.
 *
 * Campo de texto livre FUNDE (`fuseFindingText`) em vez de pular. Célula de
 * texto que o card já tinha mandado igual não é mexida de novo — senão uma
 * edição do médico depois do ditado seria desfeita a cada card novo.
 */
export function applyVoiceExamFill(
  current: EncounterExamsState,
  fill: ExamFillMap,
  prevPushed: ExamFillMap | null,
): { next: EncounterExamsState; filledCount: number } {
  fill = fitRefractionToRecord(fill, current);
  if (prevPushed) prevPushed = fitRefractionToRecord(prevPushed, current);
  // Separa o texto livre (funde) dos valores (não-destrutivo, abaixo).
  const textFill: ExamFillMap = {};
  const valueFill: ExamFillMap = {};
  for (const [mod, fields] of Object.entries(fill)) {
    for (const [key, cell] of Object.entries(fields)) {
      if (!TEXT_FIELDS.get(mod)?.has(key)) {
        (valueFill[mod] ??= {})[key] = cell;
        continue;
      }
      const atual = current.entries.find((e) => e.moduleId === mod)?.values[key] ?? {};
      const velho = prevPushed?.[mod]?.[key] ?? {};
      const out: { OD?: string; OE?: string; mono?: string } = {};
      for (const eye of ['OD', 'OE', 'mono'] as const) {
        const val = cell[eye];
        if (!val || velho[eye] === val) continue;
        // Normal do card já no campo: o achado ditado toma o lugar dele.
        const anterior = velho[eye] ?? (isNormalFinding(mod, atual[eye]) ? atual[eye] : undefined);
        out[eye] = fuseFindingText(mod, atual[eye] ?? '', val, anterior);
      }
      if (out.OD || out.OE || out.mono) (textFill[mod] ??= {})[key] = out;
    }
  }

  let base = current;
  if (prevPushed) {
    // Esvazia as células ainda iguais ao que o ditado pôs — o applyExamFill
    // abaixo regrava as que o card ainda traz, com o valor novo. As que o card
    // não traz mais (apagadas no "Editar" do card, 01/10/2026) ficam vazias.
    // O que o médico digitou por cima não é igual ao velho: fica.
    //
    // Texto livre que o card ainda traz é fundido acima. O achado que o card
    // não traz mais (× no card, 07/10/2026) só sai se o campo tem SÓ ele —
    // fundido no texto do médico, não dá pra separar: fica.
    base = {
      ...current,
      entries: current.entries.map((e) => {
        const antes = prevPushed[e.moduleId];
        if (!antes) return e;
        const values = { ...e.values };
        for (const [key, velho] of Object.entries(antes)) {
          if (!values[key]) continue;
          const texto = TEXT_FIELDS.get(e.moduleId)?.has(key);
          const v = { ...values[key] };
          for (const eye of ['OD', 'OE', 'mono'] as const) {
            const old = velho[eye];
            if (!old) continue;
            if (texto) {
              if (fill[e.moduleId]?.[key]?.[eye]) continue;
              const tem = v[eye]?.trim();
              if (tem === old.trim() || tem === fuseFindingText(e.moduleId, '', old).trim()) v[eye] = '';
              continue;
            }
            if (v[eye]?.trim() === old) v[eye] = '';
          }
          values[key] = v;
        }
        return { ...e, values };
      }),
    };
  }
  const a = applyExamFill(base, valueFill, VOICE_APPLY_MODULES, { autoAdd: true, overwrite: false });
  // Texto já fundido acima: grava por cima (o que o médico tinha está dentro).
  const b = applyExamFill(a.next, textFill, VOICE_APPLY_MODULES, { autoAdd: true, overwrite: true });
  return { next: b.next, filledCount: a.filledCount + b.filledCount };
}

/**
 * Botão "Substituir" do card (pedido de 01/10/2026): o valor do card troca o
 * que estiver no campo — número e texto livre (sem fundir). Campo que o card
 * não traz fica como estava. Sem IA.
 */
export function replaceVoiceExamFill(
  current: EncounterExamsState,
  fill: ExamFillMap,
): { next: EncounterExamsState; filledCount: number } {
  return applyExamFill(current, fitRefractionToRecord(fill, current), VOICE_APPLY_MODULES, {
    autoAdd: true,
    overwrite: true,
  });
}

/** Linhas do card "Exames do atendimento" sem as que têm card próprio (Biomicroscopia/Fundoscopia/Mapeamento). */
export function examesSemNormais(fill: ExamFillMap): ExamFillRow[] {
  return describeExamFill(fill)
    .map((m) => ({ ...m, fields: m.fields.filter((f) => !isNormalCardField(m.moduleId, f.key)) }))
    .filter((m) => m.fields.length > 0);
}

/** Card "Exames do atendimento" que só carrega o que já aparece nos cards Biomicroscopia/Fundoscopia. */
export function soNosCardsDoExame(draft: VoiceDraft): boolean {
  return draft.kind === 'examesAtendimento' && draft.gaps.length === 0 && examesSemNormais(draft.fill).length === 0;
}
